import { HHTableCell, HHTableRow } from '@hinge-health/react-component-library';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildConfigurationDetailPath,
  CONFIGURATION_STATUS_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { Configuration } from '../domain/types';
import { RelativeTimeCell } from './relative-time-cell';
import { StatusChip } from './status-chip';
import { TableCard, tableCardTestIds } from './table-card';

const TEST_ID_PREFIX = 'pdg-provider-configurations-table';
const ids = tableCardTestIds(TEST_ID_PREFIX);

export const PROVIDER_CONFIGURATIONS_TABLE_TEST_ID = ids.table;
export const PROVIDER_CONFIGURATIONS_TABLE_EMPTY_TEST_ID = ids.empty;
export const PROVIDER_CONFIGURATIONS_TABLE_SKELETON_TEST_ID = ids.skeleton;
export const PROVIDER_CONFIGURATIONS_TABLE_ERROR_TEST_ID = ids.error;
export const PROVIDER_CONFIGURATIONS_TABLE_FOOTER_TEST_ID = ids.footer;
export const providerConfigurationRowTestId = (id: string): string =>
  `pdg-provider-configuration-row-${id}`;
export const providerConfigurationLinkTestId = (id: string): string =>
  `pdg-provider-configuration-link-${id}`;
export const providerConfigurationStatusTestId = (id: string): string =>
  `pdg-provider-configuration-status-${id}`;

export const PROVIDER_CONFIGURATIONS_EMPTY_MESSAGE =
  'No configuration matches this bucket, so its files are rejected at the gate. Create one.';
export const PROVIDER_CONFIGURATIONS_ERROR_MESSAGE =
  'Something went wrong loading configurations.';

const COLUMN_HEADERS = ['Name', 'Status', 'Updated'];

interface ProviderConfigurationsTableProps {
  configurations: Configuration[];
  /** True while a fetch is in flight. Only blanks the table on first load. */
  isLoading?: boolean;
  /** True when the last fetch failed. Rows already loaded stay visible. */
  hasError?: boolean;
}

/**
 * The configurations that claim this bucket. Read-only on purpose: status
 * transitions live on the configurations pages, where the confirmation
 * dialog and its mutation state already are.
 */
export const ProviderConfigurationsTable = ({
  configurations,
  isLoading = false,
  hasError = false,
}: ProviderConfigurationsTableProps): JSX.Element => {
  const basePath = useBasePath();

  return (
    <TableCard
      testIdPrefix={TEST_ID_PREFIX}
      ariaLabel="Configurations for this provider"
      headers={COLUMN_HEADERS}
      count={configurations.length}
      noun={{ singular: 'configuration', plural: 'configurations' }}
      emptyMessage={PROVIDER_CONFIGURATIONS_EMPTY_MESSAGE}
      errorMessage={PROVIDER_CONFIGURATIONS_ERROR_MESSAGE}
      isLoading={isLoading}
      hasError={hasError}
    >
      {configurations.map(configuration => (
        <HHTableRow
          key={configuration.id}
          hover
          data-testid={providerConfigurationRowTestId(configuration.id)}
        >
          <HHTableCell>
            <Link
              component={RouterLink}
              to={`${basePath}/${buildConfigurationDetailPath(
                configuration.id,
              )}`}
              underline="hover"
              color="text.primary"
              variant="body2"
              sx={{ fontWeight: 500 }}
              data-testid={providerConfigurationLinkTestId(configuration.id)}
            >
              {configuration.name}
            </Link>
          </HHTableCell>
          <HHTableCell>
            <StatusChip
              status={configuration.status}
              label={CONFIGURATION_STATUS_LABELS[configuration.status]}
              testId={providerConfigurationStatusTestId(configuration.id)}
            />
          </HHTableCell>
          <HHTableCell>
            <RelativeTimeCell isoDate={configuration.updatedAt} />
          </HHTableCell>
        </HHTableRow>
      ))}
    </TableCard>
  );
};
